import { ref, watch, computed } from "@vue/composition-api";
import store from "@/store";
import router from "@/router";

// Notification
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

const MEMBERS_LIST_QUERY_KEY = "membersListQuery";
const DEFAULT_PER_PAGE = 20;

function parseListQuery(query = {}) {
  const page = parseInt(query.p, 10);
  const limit = parseInt(query.l, 10);

  return {
    searchQuery: query.q != null ? String(query.q) : "",
    currentPage: Number.isFinite(page) && page > 0 ? page : 1,
    perPage:
      Number.isFinite(limit) && limit > 0 ? limit : DEFAULT_PER_PAGE,
  };
}

function getStoredListQuery() {
  try {
    const raw = sessionStorage.getItem(MEMBERS_LIST_QUERY_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
}

function saveListQuery(query) {
  sessionStorage.setItem(MEMBERS_LIST_QUERY_KEY, JSON.stringify(query));
}

function hasListFilters(query = {}) {
  return Boolean(query.q || query.p || query.l);
}

function buildListQuery({ searchQuery, currentPage, perPage }) {
  const query = {};
  if (searchQuery) query.q = searchQuery;
  if (currentPage !== 1) query.p = String(currentPage);
  if (perPage !== DEFAULT_PER_PAGE) query.l = String(perPage);
  return query;
}

function queriesMatch(a = {}, b = {}) {
  return (
    String(a.q || "") === String(b.q || "") &&
    String(a.p || "") === String(b.p || "") &&
    String(a.l || "") === String(b.l || "")
  );
}

export default function useUsersList() {
  // Use toast
  const toast = useToast();

  const refUserListTable = ref(null);

  // Table Handlers
  const tableColumns = [
    { key: "index", label: "Index", sortable: false },
    { key: "createdAt", label: "Registration Date", sortable: false },
    { key: "fullname", label: "Member", sortable: false },
    { key: "status", label: "Status", sortable: false },
    { key: "platformdepartmentname", label: "Department", sortable: false },
  ];

  const routeQuery = router.currentRoute.query || {};
  const initialQuery = hasListFilters(routeQuery)
    ? parseListQuery(routeQuery)
    : parseListQuery(getStoredListQuery() || {});

  const perPage = ref(initialQuery.perPage);
  const totalUsers = ref(0);
  const currentPage = ref(initialQuery.currentPage);
  const perPageOptions = [20, 50, 100];
  const searchQuery = ref(initialQuery.searchQuery);
  const sortBy = ref("fullname");
  const isSortDirDesc = ref(true);
  const roleFilter = ref(null);
  const planFilter = ref(null);
  const statusFilter = ref(null);
  let isSyncingQuery = false;

  const dataMeta = computed(() => {
    const localItemsCount = refUserListTable.value
      ? refUserListTable.value.localItems.length
      : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalUsers,
    };
  });

  const refetchData = () => {
    if (refUserListTable.value) {
      refUserListTable.value.refresh();
    }
  };

  const syncQueryToRoute = () => {
    const query = buildListQuery({
      searchQuery: searchQuery.value,
      currentPage: currentPage.value,
      perPage: perPage.value,
    });

    saveListQuery(query);

    if (router.currentRoute.name !== "client-members-page") {
      return;
    }

    if (queriesMatch(router.currentRoute.query, query)) {
      return;
    }

    isSyncingQuery = true;
    router
      .replace({ name: "client-members-page", query })
      .catch(() => {})
      .finally(() => {
        isSyncingQuery = false;
      });
  };

  // Restore filters into the URL when returning via breadcrumb (no query)
  if (
    !hasListFilters(routeQuery) &&
    hasListFilters(buildListQuery(initialQuery))
  ) {
    syncQueryToRoute();
  } else {
    saveListQuery(
      buildListQuery({
        searchQuery: searchQuery.value,
        currentPage: currentPage.value,
        perPage: perPage.value,
      })
    );
  }

  watch(
    [currentPage, perPage, searchQuery, roleFilter, planFilter, statusFilter],
    () => {
      if (!isSyncingQuery) {
        syncQueryToRoute();
      }
      refetchData();
    }
  );

  const fetchInstructers = (ctx, callback) => {
    store
      .dispatch("apps-members/fetchAllMembers", {
        currentPage: currentPage.value,
        perPage: perPage.value,
        searchQuery: searchQuery.value,
      })
      .then((response) => {
        const { users, total } = response.data;
        callback(users);
        totalUsers.value = total;
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error fetching users list",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveUserRoleVariant = (role) => {
    if (role === "subscriber") return "primary";
    if (role === "author") return "warning";
    if (role === "maintainer") return "success";
    if (role === "editor") return "info";
    if (role === "admin") return "danger";
    return "primary";
  };

  const resolveUserRoleIcon = (role) => {
    if (role === "subscriber") return "UserIcon";
    if (role === "author") return "SettingsIcon";
    if (role === "maintainer") return "DatabaseIcon";
    if (role === "editor") return "Edit2Icon";
    if (role === "admin") return "ServerIcon";
    return "UserIcon";
  };

  const resolveUserStatusVariant = (status) => {
    if (status == 0) return "warning";
    if (status == 1) return "success";
    if (status == 2) return "secondary";
    if (status == 500) return "danger";
    return "success";
  };
  const resolveUserStatusVariantText = (status) => {
    if (status == null) return "active";
    if (status == 0) return "deleted";
    if (status == 1) return "active";
    if (status == 2) return "disabled";
    if (status == 500) return "blocked";
    return "primary";
  };

  return {
    fetchInstructers,
    tableColumns,
    perPage,
    currentPage,
    totalUsers,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refUserListTable,

    resolveUserRoleVariant,
    resolveUserRoleIcon,
    resolveUserStatusVariant,
    resolveUserStatusVariantText,
    refetchData,

    // Extra Filters
    roleFilter,
    planFilter,
    statusFilter,
  };
}
